import { Controller } from '@hotwired/stimulus';
import {
  batchSetNodeKVPair,
  executeActionsInDom,
  normalizeActions,
  updateProgressFromResponse,
  setDisplayBlockByIds,
  setDisplayNoneByIds,
  showFinishCardContentAndSection,
  getDusFromDom,
  isPassiveRootCardSection,
  mergeDocumentUserStateForContinueSync,
  syncAllContinueButtonsForDocument,
  shouldShowCompletionModals,
  revealFinishCaseCardSection,
} from '../utils/case_experience_in_rails';
import {
  startStudentCaseConfetti,
  stopStudentCaseConfetti,
} from '../utils/confetti';
import { trapFocus, releaseFocusTrap } from '../utils/FocusTrap';

// When finish is revealed, open post-finish roots (legacy one-column new_case_view).
export function expandPostFinishTerminalReveals(actions) {
  const revealsFinish = Array.isArray(actions) && actions.some((a) => {
    if (a?.action !== 'setNodeKVPair') return false;
    if (a.arguments?.key !== 'invisible' || a.arguments?.value !== false) return false;
    const uid = a.arguments?.uid;
    if (!uid) return false;
    if (String(uid).includes('finish_case')) return true;
    const el = document.querySelector(`[data-uid="${CSS.escape(String(uid))}"]`);
    return el?.classList?.contains('finish_case_card');
  });
  if (!revealsFinish) return actions;

  const extra = [];
  const seen = new Set(
    actions
      .filter((a) => a?.arguments?.key === 'invisible' && a.arguments?.value === false)
      .map((a) => a.arguments?.uid)
      .filter(Boolean),
  );

  const addReveal = (uid) => {
    if (!uid || seen.has(uid)) return;
    seen.add(uid);
    extra.push({
      action: 'setNodeKVPair',
      arguments: { uid, key: 'invisible', value: false },
    });
    extra.push({
      action: 'setNodeKVPair',
      arguments: { uid, key: 'collapsed', value: false },
    });
  };

  document
    .querySelectorAll(
      '.doc-section.release_notes_card, .doc-section.learning_objectives_card, .doc-section.saq_card',
    )
    .forEach((el) => addReveal(el.dataset?.uid));

  document.querySelectorAll('.doc-section[data-level="1"]').forEach((el) => {
    const title = el.querySelector('.doc-section-header-title-heading')?.textContent || '';
    if (/Learning Objectives/i.test(title) || /Release Notes/i.test(title) || /\bSAQ\b|Self[- ]?Assessment/i.test(title)) {
      addReveal(el.dataset?.uid);
    }
  });

  ['finish_case_text', 'end_of_case_text'].forEach((uid) => {
    if (document.querySelector(`[data-uid="${CSS.escape(uid)}"]`)) {
      if (!seen.has(uid)) {
        seen.add(uid);
        extra.push({
          action: 'setNodeKVPair',
          arguments: { uid, key: 'invisible', value: false },
        });
      }
    }
  });

  return actions.concat(extra);
}

const AI_ASSISTANT_SELECTOR =
  '[data-controller~="ai-assistant-chat"], [data-controller~="ai-assistant-feedback"]';

// Same save as a Continue click: the opened topic's Continue is visible,
// unless that topic was already marked visited or it is an AI card.
export function appendOpenedSectionContinueVisibility(actions, dus = {}) {
  if (!Array.isArray(actions)) return actions;

  const openedUid = actions.find((action) => {
    if (action?.action !== 'setNodeKVPair') return false;
    if (action.arguments?.key !== 'invisible' || action.arguments?.value !== false) return false;
    const uid = action.arguments?.uid;
    if (!uid || typeof document === 'undefined') return false;
    const section = document.querySelector(`[data-uid="${CSS.escape(String(uid))}"]`);
    if (!section?.classList?.contains('doc-section')) return false;
    if (section.dataset?.level && section.dataset.level !== '1') return false;
    return !isPassiveRootCardSection(section);
  })?.arguments?.uid;

  if (!openedUid) return actions;

  const section = document.querySelector(`[data-uid="${CSS.escape(String(openedUid))}"]`);
  const continueEl = section?.querySelector(
    '[data-case-viewer-target="continueButton"], [data-button-action-name-value="continue"]',
  );
  const continueUid = continueEl?.id || continueEl?.getAttribute('data-uid');
  if (!continueUid) return actions;

  const alreadySet = actions.some(
    (action) =>
      action?.action === 'setNodeKVPair' &&
      action.arguments?.uid === continueUid &&
      action.arguments?.key === 'invisible',
  );
  if (alreadySet) return actions;

  const visited = dus?.[openedUid]?.visited?.value === true;
  const hasAiAssistant = Boolean(section.querySelector(AI_ASSISTANT_SELECTOR));

  return actions.concat({
    action: 'setNodeKVPair',
    arguments: { uid: continueUid, key: 'invisible', value: visited || Boolean(hasAiAssistant) },
  });
}

// The server kept a newer lock, so this click must not hide Continue on screen.
export function continueUnlockRejected(actions, responseState) {
  if (!responseState || !Array.isArray(actions)) return false;

  return actions.some((action) => {
    if (action?.action !== 'setNodeKVPair') return false;
    if (action.arguments?.key !== 'invisible' || action.arguments?.value !== false) return false;
    const uid = action.arguments?.uid;
    if (!uid || typeof document === 'undefined') return false;
    const section = document.querySelector(`[data-uid="${CSS.escape(String(uid))}"]`);
    if (!section?.classList?.contains('doc-section')) return false;
    if (section.dataset?.level && section.dataset.level !== '1') return false;
    if (isPassiveRootCardSection(section)) return false;
    return responseState[uid]?.invisible?.value === true;
  });
}

function showLowTimeModalWithFocusTrap() {
  const el = document.getElementById('low-time-new-modal');
  setDisplayBlockByIds(['low-time-new-modal']);
  if (el) {
    releaseFocusTrap(el);
    trapFocus(el);
  }
}

function hideLowTimeModalAndReleaseTrap() {
  const el = document.getElementById('low-time-new-modal');
  if (el) releaseFocusTrap(el);
  setDisplayNoneByIds(['low-time-new-modal']);
}

export default class extends Controller {
  static values = {
    uid: String,
    onClick: Array,
    name: String,
    documentId: Number,
    userId: Number,
    invisible: Boolean,
    inactive: Number,
    aiSummaryStatementAnswerCommentShown: {
      type: Boolean,
      default: false,
    },
  };

  connect() {
    this.updateButtonState();
  }

  disconnect() {
    // Clean up any pending setTimeout
    if (this._completionTimeout) {
      clearTimeout(this._completionTimeout);
      this._completionTimeout = null;
    }
    // Clean up confetti interval
    stopStudentCaseConfetti();
  }

  invisibleValueChanged() {
    this.updateButtonState();
  }

  inactiveValueChanged() {
    this.updateButtonState();
  }

  updateButtonState() {
    // Visibility
    this.element.style.display = this.invisibleValue ? 'none' : '';

    // Disabled state
    const button = this.element.querySelector('button');
    if (!button) return;

    const name = this.nameValue?.toLowerCase();
    const rawInactive = this.element.getAttribute('data-button-action-inactive-value');
    const submitWithoutPersistedInactive =
      name === 'submit' && (rawInactive === null || rawInactive === '');

    const feedbackScope =
      this.element.closest('#feedback-modal-content') ||
      this.element.closest('.doc-section.feedback_card');
    const feedbackRatingBars = feedbackScope
      ? [...feedbackScope.querySelectorAll('[data-controller~="rating-bar"]')]
      : [];
    const feedbackRatingsIncomplete =
      feedbackRatingBars.length > 0 &&
      feedbackRatingBars.some((el) => {
        const v = parseInt(el.getAttribute('data-rating-bar-current-value-value'), 10);
        return Number.isNaN(v) || v <= 0;
      });

    if (submitWithoutPersistedInactive && feedbackRatingBars.length === 0) {
      return;
    }

    let inactiveDisabled = false;
    if (!submitWithoutPersistedInactive) {
      inactiveDisabled = this.inactiveValue === 2;
    }

    const isDisabled = inactiveDisabled || feedbackRatingsIncomplete;
    button.disabled = isDisabled;
    button.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
  }

  async handleClick(event) {
    event.preventDefault();

    // Accessibility: Progress bar ARIA update and live region announcement for Continue button
    const buttonTitle = this.nameValue?.toLowerCase();
    if (buttonTitle === 'continue') {
      const progressBar = document.querySelector('.progress-container');
      if (progressBar) {
        // Get latest progress value
        let progress = parseInt(progressBar.getAttribute('aria-valuenow'), 10);
        progressBar.setAttribute('aria-valuenow', String(progress));
        let label;
        if (progress === 0 || isNaN(progress)) {
          label = 'Not Started';
        } else if (progress === 100) {
          label = '100% of the case is completed';
        } else {
          label = `${Math.round(progress)}% of the case is completed`;
        }
        progressBar.setAttribute('aria-label', label);
      }
      // Use a dedicated live region for announcement
      let alertRegion = document.getElementById('screenreader-alert');
      if (!alertRegion) {
        alertRegion = document.createElement('div');
        alertRegion.id = 'screenreader-alert';
        alertRegion.setAttribute('aria-live', 'assertive');
        alertRegion.style.position = 'absolute';
        alertRegion.style.left = '-9999px';
        alertRegion.style.height = '1px';
        alertRegion.style.width = '1px';
        alertRegion.style.overflow = 'hidden';
        document.body.appendChild(alertRegion);
      }
      if (progressBar && alertRegion) {
        // Clear live region first to force announcement
        alertRegion.textContent = '';
        setTimeout(() => {
          const announceText = progressBar.getAttribute('aria-label') || 'Progress bar focused';
          alertRegion.textContent = announceText;
        }, 1500);
      }
    }

    if (!this.hasOnClickValue || !this.onClickValue) {
      console.log('Button clicked but no onClick actions defined');
      return;
    }

    const normalizedActions = normalizeActions(this.onClickValue);

    // Run non-setNodeKVPair actions first
    normalizedActions.forEach((action) => {
      if (action.action !== 'setNodeKVPair') {
        this.processAction(action);
      }
    });

    // Batch API calls
    let nodeKvPairActions = normalizedActions.filter(
      (a) => a.action === 'setNodeKVPair',
    );
    nodeKvPairActions = this.appendFeedbackSubmitClickedAction(nodeKvPairActions);
    // new_case_view without design-system two-col: feedback onClick only opened finish_case_card;
    // finish_case_button used to reveal RN/LO/SAQ after. Expand those reveals here.
    nodeKvPairActions = expandPostFinishTerminalReveals(nodeKvPairActions);
    if (this.nameValue?.toLowerCase() === 'continue') {
      nodeKvPairActions = appendOpenedSectionContinueVisibility(nodeKvPairActions, getDusFromDom());
    }

    if (!nodeKvPairActions.length) return;

    const priorDus = getDusFromDom();
    const response = await batchSetNodeKVPair(
      nodeKvPairActions,
      this.documentIdValue,
      this.userIdValue,
    );

    this.updateUiPostApiCall(response, nodeKvPairActions, priorDus);
  }

  appendFeedbackSubmitClickedAction(actions) {
    if (!this._isFeedbackSubmitButton()) return actions;
    const buttonUid = this.uidValue;
    if (!buttonUid) return actions;

    const alreadyPresent = actions.some(
      (a) =>
        a.action === 'setNodeKVPair' &&
        a.arguments?.uid === buttonUid &&
        a.arguments?.key === 'clicked',
    );
    if (alreadyPresent) return actions;

    return [
      ...actions,
      {
        action: 'setNodeKVPair',
        arguments: { uid: buttonUid, key: 'clicked', value: true },
      },
    ];
  }

  _isFeedbackSubmitButton() {
    if (this.uidValue === 'feedback_card_1_button') return true;
    return this.nameValue?.toLowerCase() === 'submit_&_finish_case';
  }

  updateUiPostApiCall(response, actions, priorDus = {}) {
    if (response) {
      updateProgressFromResponse(response);
    }

    if (
      this.nameValue?.toLowerCase() === 'continue' &&
      continueUnlockRejected(actions, response?.state)
    ) {
      return;
    }

    const showCompletion = shouldShowCompletionModals({
      actions,
      response,
      priorDus,
      buttonElement: this.element,
      nameValue: this.nameValue,
    });

    this.disableButton();
    this.handleSubmitUi(this.element);
    if (this.nameValue === 'continue') {
      this.pauseVideosInSectionContainingButton();
    }
    executeActionsInDom(actions, this.application);

    if (showCompletion) {
      revealFinishCaseCardSection(this.application);
      requestAnimationFrame(() => {
        this.handleCompletionModals();
      });
    }

    const nameLower = this.nameValue?.toLowerCase() || '';
    if (nameLower === 'submit' || nameLower.includes('finish_case')) {
      const domState = getDusFromDom();
      const apiState = response?.state && typeof response.state === 'object' ? response.state : {};
      const mergedState = mergeDocumentUserStateForContinueSync(domState, apiState);
      syncAllContinueButtonsForDocument(mergedState);
    }

    if (this.nameValue === 'continue') {
      this.scrollToNewlyOpenedSection(actions);
    }
  }

  pauseVideosInSectionContainingButton() {
    const section = this.element.closest('.doc-section');
    if (!section) return;
    section.querySelectorAll('video').forEach((video) => {
      try {
        video.pause();
      } catch {
        // Ignore (e.g. media not loaded)
      }
    });
  }

  scrollToNewlyOpenedSection(actions) {
    const openAction = actions.find(
      (a) => a.arguments?.key === 'collapsed' && a.arguments?.value === false,
    );
    const sectionUid = openAction?.arguments?.uid;
    if (!sectionUid) return;

    const offsetFromTop = this.calculateTopFixedOffset();
    setTimeout(() => {
      const section = document.querySelector(`[data-uid="${sectionUid}"]`);
      const header = section?.querySelector('.doc-section-header') || section?.firstElementChild;
      if (!header) return;

      const y = header.getBoundingClientRect().top + window.scrollY - offsetFromTop;
      window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
    }, 150);
  }

  calculateTopFixedOffset() {
    const maxTopBarHeight = Math.min(400, window.innerHeight * 0.25);
    const candidates = document.querySelectorAll(
      'header, .app-header, .navigation, .navbar, .navbar-primary, .top-nav, .topbar, .sticky-info, .breadcrumb-container, [data-fixed-top]',
    );
    let maxBottom = 0;
    let stickyBreadcrumbHeight = 0;

    candidates.forEach((el) => {
      const style = window.getComputedStyle(el);
      if (!['fixed', 'sticky'].includes(style.position)) return;
      if (style.display === 'none' || style.visibility === 'hidden') return;

      const rect = el.getBoundingClientRect();
      if (rect.height <= 0) return;

      if (el.matches('.sticky-info, .breadcrumb-container')) {
        stickyBreadcrumbHeight = Math.max(stickyBreadcrumbHeight, rect.height);
        return;
      }

      if (rect.bottom <= 0) return;
      if (rect.top > 0) return;
      if (rect.bottom > maxTopBarHeight) return;

      maxBottom = Math.max(maxBottom, rect.bottom);
    });

    return Math.ceil(maxBottom) + Math.ceil(stickyBreadcrumbHeight);
  }

  handleCompletionModals() {
    const documentContainer = document.getElementById('document-container');
    const isStudent =
      documentContainer?.dataset?.caseViewerIsStudentValue === 'true';
    if (!isStudent) return;

    const percentElement = document.getElementById(
      'percentRecommendedTimeField',
    );
    const percentOfRecommendedTime = parseFloat(
      percentElement?.value || '100',
    );

    setDisplayNoneByIds(['finish-card-section']);

    const lowTimeModalEl = document.getElementById('low-time-new-modal');
    const caseCompletionModalEl = document.getElementById('case-completion-modal');
    releaseFocusTrap(lowTimeModalEl);
    releaseFocusTrap(caseCompletionModalEl);

    if (percentOfRecommendedTime < 25) {
      setDisplayNoneByIds(['case-completion-modal']);
      showLowTimeModalWithFocusTrap();
      return;
    }

    void startStudentCaseConfetti();

    hideLowTimeModalAndReleaseTrap();
    setDisplayBlockByIds(['case-completion-modal']);
    setDisplayNoneByIds(['finish-card-content']);

    if (caseCompletionModalEl) {
      trapFocus(caseCompletionModalEl);
    }

    const hasStudentChoiceCasesInput = document.querySelector('input[name="hasStudentChoiceCases"]');
    const hasStudentChoiceCasesValue = hasStudentChoiceCasesInput?.value === 'true';
    if (!hasStudentChoiceCasesValue) {
      this._completionTimeout = setTimeout(() => {
        stopStudentCaseConfetti();
        showFinishCardContentAndSection();
        this._completionTimeout = null;
      }, 5000);
    }
  }

  disableButton() {
    const innerButton = this.element.querySelector('button');
    if (innerButton) innerButton.disabled = true;
  }

  handleSubmitUi(submitButton) {
    if (!submitButton || this.nameValue !== 'submit') return;

    const section = submitButton.closest('.doc-section');
    if (!section) return;

    const questionNode = section.querySelector(
      '.doc-children [data-is-node] .doc-multiple-choice-wrapper',
    );
    const answerCommentNode = section.querySelector(
      '.doc-children .answer_comment',
    );

    if (questionNode) {
      this.showGradedInfo(questionNode);
    }

    if (
      answerCommentNode &&
      !this.aiSummaryStatementAnswerCommentShownValue
    ) {
      answerCommentNode.classList.remove('hidden');
    }
  }

  showGradedInfo(questionNode) {
    const gradedInfos = questionNode.querySelectorAll(
      '.doc-multiple-choice-answer-graded-info',
    );

    gradedInfos.forEach((info) => {
      const parentLi = info.closest('li');
      if (!parentLi) return;

      if (parentLi.dataset.answerCorrect === 'true') {
        info.classList.remove('hidden');
        info.style.display = '';
      } else if (parentLi.dataset.answerCorrect === 'false') {
        info.classList.remove('hidden');
        info.classList.add('invisible');
      }
    });

    const gradedFeedback =
      questionNode.querySelector('.graded-feedback');
    gradedFeedback?.classList.remove('hidden');
  }

  processAction(action) {
    if (!action?.action) return;
    const { action: type, arguments: args } = action;
    switch (type) {
      case 'scrollTo':
        this.scrollTo(args);
        break;
      case 'openExternalLink':
        this.openExternalLink(args);
        break;
      case 'openInternalLink':
        this.openInternalLink(args);
        break;
      case 'setNodeKVPair':
        this.setNodeKVPair(args);
        break;
      default:
        console.log(`Unknown action: ${type}`);
    }
  }

  setNodeKVPair(args) {
    batchSetNodeKVPair(
      [{ action: 'setNodeKVPair', arguments: args }],
      this.documentIdValue,
      this.userIdValue,
    );
  }

  scrollTo(args) {
    const nodeUid = args?.uid;
    if (!nodeUid) return;
    setTimeout(() => {
      const element = document.querySelector(`[data-uid="${nodeUid}"]`);
      if (element) {
        const yOffset = -125; // Offset for fixed header
        const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 100);
  }

  openExternalLink(args) {
    if (args?.url) {
      window.open(args.url, '_blank');
    }
  }

  openInternalLink(args) {
    if (args?.path) {
      window.open(args.path, '_blank');
    }
  }

}
